import React, { useState } from 'react';
import format from 'date-fns/format';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';

import {
    Button,
    FormField,
    FormHelp,
    Label,
    Textarea,
    TextButton,
    Spacer,
    Flex,
} from '@zillow/constellation';
import { Datepicker } from '@zillow/zrs-rentals';

import { PropertiesSelectors } from 'app/shared/selectors';
import PropertiesActions from 'app/shared/actions/PropertiesActions';
import KeyCodeConstants from 'app/shared/constants/KeyCodeConstants';
import ModalActions from 'app/shared/actions/ModalActions';
import {
    StyledMDActionsContainer,
    StyledMDContainer,
    StyledMDContent,
    StyledMDContentContainer,
} from 'app/shared/containers/ModalContainer/StyledComponents';

const propTypes = {
    action: PropTypes.string,
    defaultOffer: PropTypes.shape({
        offerIdEncoded: PropTypes.string,
        description: PropTypes.string,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }),
    specialOffers: PropTypes.array.isRequired,
    updateFormData: PropTypes.func.isRequired,
};

const defaultProps = {
    action: 'add',
    defaultOffer: {
        description: '',
        startDate: '',
        endDate: '',
        offerIdEncoded: '',
    },
};

const SpecialOffersModal = ({
    action,
    defaultOffer,
    specialOffers,
    updateFormData,
}) => {
    const dispatch = useDispatch();
    const { listingAlias } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const [specialOffer, setSpecialOffer] = useState(defaultOffer);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState();

    const {
        startDateYyyyMmDd,
        endDateYyyyMmDd,
        offerIdEncoded,
        description,
    } = specialOffer;

    const onCancel = () => dispatch(ModalActions.hideModal());

    const handleSubmit = () => {
        setIsSubmitting(true);

        const queryParams = {
            listingId: listingAlias,
            includeListingDetails: false,
        };

        const bodyParams = {
            ...(startDateYyyyMmDd) && { startDate: startDateYyyyMmDd },
            ...(endDateYyyyMmDd) && { endDate: endDateYyyyMmDd },
            ...(offerIdEncoded) && { offerIdEncoded },
            description,
        };

        return dispatch(PropertiesActions.createOrUpdateSpecialOffer(bodyParams, queryParams))
            .then((response) => {
                setIsSubmitting(false);
                const specialOffersCopy = cloneDeep(specialOffers);

                if (action === 'edit') {
                    const indexToUpdate = findIndex(
                        specialOffersCopy, offer => offer.offerIdEncoded === offerIdEncoded
                    );

                    if (indexToUpdate === -1) {
                        return setErrors([{ message: 'There was an error saving your offer. Please try again.' }]);
                    }

                    specialOffersCopy[indexToUpdate] = { ...response };
                } else {
                    specialOffersCopy.push({ ...response });
                }

                updateFormData({ specialOffers: specialOffersCopy });
                dispatch(ModalActions.hideModal());
            })
            .catch(errorRMWModel => setErrors(errorRMWModel.data.errors))
            .finally(() => setIsSubmitting(false));
    };

    const onChange = (data) => {
        setErrors();
        setSpecialOffer({ ...specialOffer, ...data });
    };


    const isStartDateEnabled = (renderedDate) => {
        if (!endDateYyyyMmDd || renderedDate === null) {
            return true;
        }

        return new Date(renderedDate) <= new Date(endDateYyyyMmDd);
    };

    const isEndDateEnabled = (renderedDate) => {
        if (!startDateYyyyMmDd || renderedDate === null) {
            return true;
        }

        return new Date(renderedDate) >= new Date(startDateYyyyMmDd);
    };

    const onKeyUp = (event) => {
        if (event.keyCode === KeyCodeConstants.ENTER ||
            event.keyCode === KeyCodeConstants.SPACE) {
            event.target.click();
        }
    };

    return (
        <StyledMDContainer>
            <StyledMDContentContainer>
                <StyledMDContent fullWidth={true}>
                    <FormField>
                        <Label>Offer start date</Label>
                        <Datepicker
                            onChange={startDate => onChange({ startDateYyyyMmDd: format(startDate, 'YYYY-MM-DD') })}
                            getIsValidDate={renderedDate => isStartDateEnabled(renderedDate)}
                            inputProps={{ placeholder: 'MM/DD/YYYY' }}
                            value={startDateYyyyMmDd ? new Date(`${startDateYyyyMmDd}T10:00:00`) : null}
                        />
                    </FormField>
                    <FormField>
                        <Label>Offer end date</Label>
                        <Datepicker
                            onChange={endDate => onChange({ endDateYyyyMmDd: format(endDate, 'YYYY-MM-DD') })}
                            getIsValidDate={renderedDate => isEndDateEnabled(renderedDate)}
                            inputProps={{ placeholder: 'MM/DD/YYYY' }}
                            value={endDateYyyyMmDd ? new Date(`${endDateYyyyMmDd}T10:00:00`) : null}
                        />
                    </FormField>
                    <FormField>
                        <Label required={true}>Description</Label>
                        <Textarea
                            placeholder="Do not use all-caps, abbreviations, jargon, or special characters."
                            type="textarea"
                            maxLength="255"
                            resize="both"
                            onChange={e => onChange({ description: e.target.value })}
                            value={description || ''}
                            error={Boolean(errors)}
                        />
                        <FormHelp>Max 255 Characters</FormHelp>
                        {errors && (
                            errors.map(
                                ({ message }) => <FormHelp error={true} dangerouslySetInnerHTML={{ __html: message }} />
                            ))}
                    </FormField>
                </StyledMDContent>
            </StyledMDContentContainer>
            <StyledMDActionsContainer>
                <Flex display="flex" justifyContent="flex-end" alignItems="center">
                    <Spacer marginRight="xs">
                        <TextButton
                            aria-label="Cancel"
                            tabIndex={0}
                            role="button"
                            className="SpecialOffersModal-cancel"
                            onClick={onCancel}
                            onKeyUp={onKeyUp}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </TextButton>
                    </Spacer>
                    <Button
                        aria-label="Save"
                        onClick={handleSubmit}
                        disabled={!description || errors || isSubmitting}
                        buttonType="primary"
                    >
                        Save
                    </Button>
                </Flex>
            </StyledMDActionsContainer>
        </StyledMDContainer>
    );
};

SpecialOffersModal.propTypes = propTypes;
SpecialOffersModal.defaultProps = defaultProps;

export default SpecialOffersModal;
